import React, { useEffect, useState } from 'react';
import { Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { rejectSupplierOrderConfirmationDraft } from '../api/supplierOrdersApi';
import { getSupplierOrderErrorMessage } from '../supplierOrderErrorMessages';
import { useFeedback } from '../../../utils/feedback';

const { Paragraph, Text } = Typography;
const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 500;

interface SupplierOrderDraftRejectModalProps {
  open: boolean;
  supplierOrderId: string;
  onClose: () => void;
  onRejected: () => void | Promise<void>;
}

/**
 * „Odrzuć” of a confirmation draft ZD on its card (decision 2026-10-04 pkt 4):
 * nothing is ordered and the customer-order owner sees the reason in the ZK
 * history. The reason is required (3–500 characters, backend rule).
 */
const SupplierOrderDraftRejectModal: React.FC<SupplierOrderDraftRejectModalProps> = ({
  open, supplierOrderId, onClose, onRejected,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const feedback = useFeedback();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= MIN_REASON_LENGTH && trimmed.length <= MAX_REASON_LENGTH;
  const submit = async () => {
    if (!reasonValid) return;
    setSubmitting(true);
    try {
      await rejectSupplierOrderConfirmationDraft(supplierOrderId, trimmed);
      feedback.success(t('orders:supplierOrders.drafts.rejectSuccessShort'));
      onClose();
      await onRejected();
    } catch (error) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.drafts.rejectError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('orders:supplierOrders.drafts.rejectTitleShort')}
      okText={t('orders:supplierOrders.drafts.rejectConfirm')}
      cancelText={t('common:cancel')}
      okButtonProps={{ danger: true, disabled: !reasonValid, loading: submitting }}
      onOk={() => void submit()}
      onCancel={() => { if (!submitting) onClose(); }}
      destroyOnHidden
    >
      <Paragraph>{t('orders:supplierOrders.drafts.rejectDescription')}</Paragraph>
      <label htmlFor="supplier-order-draft-reject-reason">
        <Text strong>{t('orders:supplierOrders.drafts.rejectReason')}</Text>
      </label>
      <Input.TextArea
        id="supplier-order-draft-reject-reason"
        rows={3}
        maxLength={MAX_REASON_LENGTH}
        showCount
        value={reason}
        placeholder={t('orders:supplierOrders.drafts.rejectReasonPlaceholder')}
        onChange={(event) => setReason(event.target.value)}
        style={{ marginTop: 6 }}
      />
    </Modal>
  );
};

export default SupplierOrderDraftRejectModal;
